import { useQuery } from '@tanstack/react-query';
import { client } from './apiClient';
import { User, transformRawUser } from '../types/api';
import { setUserContext } from './sentry';

const USER_CACHE_TIME = 5 * 60 * 1000; // 5 minutes

export interface UseCurrentUserResult {
  currentUser: User | undefined;
  isLoading: boolean;
  error: Error | null;
  isError: boolean;
  refetch: ReturnType<typeof useQuery<unknown, Error, User>>['refetch'];
  isFetching: boolean;
  isSuccess: boolean;
}

export function useCurrentUser(): UseCurrentUserResult {
  const { data, isLoading, error, isError, refetch, isFetching, isSuccess } = useQuery<
    unknown,
    Error,
    User
  >({
    queryKey: ['currentUser'],
    queryFn: async () => {
      const response = await client.userSession();

      if (!response.user) {
        throw new Error('User data not available');
      }

      setUserContext(response.user.id);
      return transformRawUser(response.user);
    },
    staleTime: USER_CACHE_TIME,
  });

  if (data && !isLoading && !isError) {
    setUserContext(data.id);
  }
  return {
    currentUser: data,
    isLoading,
    error,
    isError,
    refetch,
    isFetching,
    isSuccess,
  };
}

export async function isAuthenticated(): Promise<boolean> {
  try {
    const response = await client.userSession();
    return !!response.user;
  } catch {
    return false;
  }
}
